<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import countries from 'shared/constants/countries';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  email: { type: String, default: '' },
  companyId: { type: [Number, String], default: '' },
  additionalAttributes: { type: Object, default: () => ({}) },
  phoneNumber: { type: String, default: '' },
  thumbnail: { type: String, default: '' },
  availabilityStatus: { type: String, default: null },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  selectable: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'updateContact',
  'showContact',
  'select',
  'avatarHover',
]);

const { t } = useI18n();

const contactsFormRef = ref(null);

const getInitialContactData = () => ({
  id: props.id,
  name: props.name,
  email: props.email,
  companyId: props.companyId,
  phoneNumber: props.phoneNumber,
  additionalAttributes: props.additionalAttributes,
});

const contactData = ref(getInitialContactData());

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const countriesMap = computed(() => {
  return countries.reduce((acc, country) => {
    acc[country.id] = country;
    return acc;
  }, {});
});

const countryDetails = computed(() => {
  const attributes = props.additionalAttributes || {};
  const { country, countryCode, city } = attributes;

  if (!country && !countryCode) return null;

  const activeCountry =
    countriesMap.value[country] || countriesMap.value[countryCode];

  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    city: city ? `${city},` : null,
    name: activeCountry.name,
  };
});

const formattedLocation = computed(() => {
  if (!countryDetails.value) return '';

  return [countryDetails.value.city, countryDetails.value.name]
    .filter(Boolean)
    .join(' ');
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = () => {
  emit('updateContact', contactData.value);
};

const onClickExpand = () => {
  emit('toggle');
  contactData.value = getInitialContactData();
};

const onClickViewDetails = () => emit('showContact', props.id);

const toggleSelect = checked => {
  emit('select', checked);
};

const handleAvatarHover = isHovered => {
  emit('avatarHover', isHovered);
};
</script>

<template>
  <div class="relative">
    <CardLayout
      :key="id"
      layout="row"
      class="relative !rounded-none !bg-auth-surface !outline-0 transition-colors duration-150 before:pointer-events-none hover:!bg-auth-soft dark:!bg-auth-surface-dark dark:hover:!bg-auth-soft-dark [&>div:first-child]:!gap-4 [&>div:first-child]:!px-4 [&>div:first-child]:!py-4 sm:[&>div:first-child]:!px-5"
      :class="{
        '!bg-auth-canvas before:absolute before:inset-y-3 before:start-0 before:w-px before:rounded-full before:bg-auth-action hover:!bg-auth-canvas dark:!bg-auth-border-dark/40 dark:before:bg-auth-accent dark:hover:!bg-auth-border-dark/40':
          isSelected,
      }"
    >
      <div class="flex min-w-0 flex-1 items-center justify-start gap-4">
        <div
          class="relative"
          @mouseenter="handleAvatarHover(true)"
          @mouseleave="handleAvatarHover(false)"
        >
          <Avatar
            :name="name"
            :src="thumbnail"
            :size="42"
            :status="availabilityStatus"
            hide-offline-status
          >
            <template v-if="selectable" #overlay="{ size }">
              <label
                class="absolute inset-0 z-10 flex cursor-pointer items-center justify-center rounded-full border border-auth-border bg-auth-surface/90 dark:border-auth-border-dark dark:bg-auth-surface-dark/90"
                :style="{ width: `${size}px`, height: `${size}px` }"
                @click.stop
              >
                <Checkbox
                  :model-value="isSelected"
                  @change="event => toggleSelect(event.target.checked)"
                />
              </label>
            </template>
          </Avatar>
        </div>
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span
              class="truncate text-sm font-semibold tracking-[-0.01em] text-auth-text dark:text-auth-text-dark sm:text-base"
            >
              {{ name }}
            </span>
            <span class="inline-flex items-center gap-1">
              <span
                v-if="additionalAttributes?.companyName"
                class="i-ph-building-light mb-0.5 size-4 text-auth-muted dark:text-auth-muted-dark"
              />
              <span
                v-if="additionalAttributes?.companyName"
                class="truncate text-xs text-auth-muted dark:text-auth-muted-dark sm:text-sm"
              >
                {{ additionalAttributes.companyName }}
              </span>
            </span>
          </div>
          <div
            class="flex flex-wrap items-center justify-start gap-x-3 gap-y-1"
          >
            <div v-if="email" class="max-w-72 truncate" :title="email">
              <span
                class="text-xs text-auth-muted dark:text-auth-muted-dark sm:text-sm"
              >
                {{ email }}
              </span>
            </div>
            <div
              v-if="email"
              class="h-3 w-px truncate bg-auth-border dark:bg-auth-border-dark"
            />
            <span
              v-if="phoneNumber"
              class="truncate text-xs text-auth-muted dark:text-auth-muted-dark sm:text-sm"
            >
              {{ phoneNumber }}
            </span>
            <div
              v-if="phoneNumber"
              class="h-3 w-px truncate bg-auth-border dark:bg-auth-border-dark"
            />
            <span
              v-if="countryDetails"
              class="inline-flex items-center gap-2 truncate text-xs text-auth-muted dark:text-auth-muted-dark sm:text-sm"
            >
              <Flag :country="countryDetails.countryCode" class="size-3.5" />
              {{ formattedLocation }}
            </span>
            <div
              v-if="countryDetails"
              class="h-3 w-px truncate bg-auth-border dark:bg-auth-border-dark"
            />
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
              variant="link"
              size="xs"
              class="!text-auth-action hover:!text-auth-action-hover dark:!text-auth-accent dark:hover:!text-auth-accent-hover"
              @click="onClickViewDetails"
            />
          </div>
        </div>
      </div>

      <Button
        icon="i-lucide-chevron-down"
        variant="ghost"
        color="slate"
        size="xs"
        class="!rounded-lg !text-auth-muted transition-transform hover:!bg-auth-canvas hover:!text-auth-text dark:!text-auth-muted-dark dark:hover:!bg-auth-border-dark dark:hover:!text-auth-text-dark"
        :class="{ 'rotate-180': isExpanded }"
        @click="onClickExpand"
      />

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div
              class="flex flex-col gap-6 border-t border-auth-border bg-auth-soft/60 p-5 dark:border-auth-border-dark dark:bg-auth-soft-dark/60 sm:p-6"
            >
              <ContactsForm
                ref="contactsFormRef"
                :contact-data="contactData"
                @update="handleFormUpdate"
              />
              <div>
                <Button
                  :label="
                    t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')
                  "
                  size="sm"
                  :is-loading="isUpdating"
                  :disabled="isUpdating || isFormInvalid"
                  class="!rounded-xl !bg-auth-action !px-4 !text-white hover:!bg-auth-action-hover dark:!bg-auth-accent dark:!text-auth-brand dark:hover:!bg-auth-accent-hover"
                  @click="handleUpdateContact"
                />
              </div>
            </div>
            <ContactDeleteSection
              :selected-contact="{
                id: props.id,
                name: props.name,
              }"
            />
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
